<script lang="ts" setup>
const emit = defineEmits(["close"]);
const props = defineProps({
  link: {
    type: Boolean,
    default: false,
  },
});
const {
  createWallet,
  getUserWallet,
  updateWalletKeys,
  setUserWalletUpdateComplete,
} = useUserController();
const { toaster } = useToaster();
const showWalletSecurityNotice = ref(false);
const showKeysSocialAccount = ref(false);
const showKeysRecoveryPhrase = ref(false);
const showWalletCreated = ref(false);
const isReturningUser = ref(useUserStore().accountAddress !== null);
const isLoading = ref(false);
const isPreparingRescue = ref(true);
const password = ref<string | null>(null);
const localePath = useLocalePath();
const { ensureRegisteredRescuePublicKey } = useRescueController();

const handleContinueUpdate = () => {
  showWalletSecurityNotice.value = false;
  showKeysSocialAccount.value = true;
};

const skipUsingSocial = () => {
  showKeysSocialAccount.value = false;
  showKeysRecoveryPhrase.value = true;
};

const backToUsingSocial = () => {
  showKeysRecoveryPhrase.value = false;
  showKeysSocialAccount.value = true;
};

const submitPasswordsAndCreateWallet = async ({
  method,
  value,
}: {
  method: UserKeyMethod;
  value: string;
}) => {
  try {
    showKeysRecoveryPhrase.value = false;
    showKeysSocialAccount.value = false;
    isLoading.value = true;
    if (!password.value) {
      const gfalid = useCookie(`gfalid-${useUserStore().id}`);
      if (gfalid.value) {
        const parsedSecret = await parseSecret(gfalid.value);
        if (parsedSecret?.type === "password")
          password.value = parsedSecret.value;
      }
    }
    let response;
    if (isReturningUser.value) {
      response = await updateWalletKeys({
        method,
        value,
        password: password.value || null,
      });
    } else {
      response = await createWallet({
        method,
        value,
        password: password.value || null,
      });
    }
    if (response.statusCode === 200) {
      showKeysSocialAccount.value = false;
      showKeysRecoveryPhrase.value = false;
      if (isReturningUser.value) setUserWalletUpdateComplete();
      showWalletCreated.value = true;
    } else if (response.statusCode === 409) {
      await handleExistingWallet();
    } else {
      throw response;
    }
    isLoading.value = false;
  } catch (error) {
    console.error("Error creating wallet", error);
    toaster("error");
    emit("close");
  }
};

const handleExistingWallet = async () => {
  try {
    const getWalletResponse = await getUserWallet();
    if (getWalletResponse.statusCode === 200) {
      showKeysSocialAccount.value = false;
      showWalletCreated.value = true;
    } else {
      throw new Error(
        `Unexpected status code: ${getWalletResponse.statusCode}`
      );
    }
  } catch (error) {
    console.error("Error getting wallet", error);
    toaster("error");
    emit("close");
  }
};

const handlePasswordType = async () => {
  const gfalid = useCookie(`gfalid-${useUserStore().id}`);
  if (!gfalid.value) throw new Error("Missing secret");
  const parsedSecret = await parseSecret(gfalid.value);
  if (!parsedSecret) throw new Error("Unable to parse secret");
  if (parsedSecret.type === "password") {
    password.value = parsedSecret.value;
    if (isReturningUser.value) showWalletSecurityNotice.value = true;
    else showKeysSocialAccount.value = true;
  } else if (
    parsedSecret.type === "google" ||
    parsedSecret.type === "apple" ||
    parsedSecret.type === "facebook"
  ) {
    submitPasswordsAndCreateWallet({
      method: parsedSecret.type,
      value: parsedSecret.value,
    });
  } else {
    throw new Error("Invalid secret type");
  }
};

const createWalletWithLinkedSocial = async () => {
  const linkSecret = useCookie(`link-${useUserStore().id}`);
  if (!linkSecret.value) throw new Error("Missing secret");
  const linkedParsedSecret = await parseSecret(linkSecret.value);
  if (!linkedParsedSecret) throw new Error("Missing secret");
  await submitPasswordsAndCreateWallet({
    method: linkedParsedSecret.type,
    value: linkedParsedSecret.value,
  });
};

const modalWidth = computed(() => {
  if (
    showWalletSecurityNotice.value ||
    showWalletCreated.value ||
    isPreparingRescue.value ||
    isLoading.value
  )
    return "medium";
  if (showKeysSocialAccount.value || showKeysRecoveryPhrase.value) return "xl";
  return "xl";
});

const handleClose = () => {
  emit("close");
};

onMounted(async () => {
  try {
    await ensureRegisteredRescuePublicKey({
      intent: { action: "create-wallet", link: props.link },
    });
    isPreparingRescue.value = false;
    if (props.link) createWalletWithLinkedSocial();
    else await handlePasswordType();
  } catch (error) {
    if ((error as Error)?.name === "RescueRegistrationRedirectedError") return;
    console.error("Error creating wallet", error);
    navigateTo(localePath("/?cause=error"));
    emit("close");
  }
});
</script>

<template>
  <BaseLayoutModal :width="modalWidth" :hide-x="true">
    <template #header>
      {{
        isReturningUser
          ? $t("main.modalUserWalletCreate.headerReturning")
          : $t("main.modalUserWalletCreate.headerNew")
      }}
      <NuxtIcon
        v-if="!isReturningUser"
        name="mdi:close"
        :alt="$t('main.imageAltTexts.closeIcon')"
        class="w-9 h-9 ml-auto text-neutral-300 cursor-pointer"
        @click="handleClose" />
    </template>
    <template #content>
      <Transition mode="out-in">
        <ModalLoader
          v-if="isPreparingRescue"
          :body-title="$t('main.modalUserWalletCreate.creatingWallet')"
          :body-subtitle="$t('main.modalUserWalletCreate.creatingWalletSubtitle')" />
        <ModalUserWalletCreateSecurityNotice
          v-else-if="showWalletSecurityNotice"
          @continue="handleContinueUpdate"
          @close="emit('close')" />
        <ModalKeysSocialAccount
          v-else-if="showKeysSocialAccount"
          @continue="submitPasswordsAndCreateWallet"
          @skip="skipUsingSocial"
          @close="emit('close')" />
        <ModalKeysRecoveryPhrase
          v-else-if="showKeysRecoveryPhrase"
          @continue="submitPasswordsAndCreateWallet"
          @back="backToUsingSocial" />
        <ModalUserWalletCreateSuccess
          v-else-if="showWalletCreated"
          :is-returning-user="isReturningUser"
          @close="emit('close')" />
        <ModalLoader
          v-else-if="isLoading"
          :body-title="
            isReturningUser
              ? $t('main.modalUserWalletCreate.returningWallet')
              : $t('main.modalUserWalletCreate.creatingWallet')
          "
          :body-subtitle="
            isReturningUser
              ? $t('main.modalUserWalletCreate.returningWalletSubtitle')
              : $t('main.modalUserWalletCreate.creatingWalletSubtitle')
          " />
      </Transition>
    </template>
  </BaseLayoutModal>
</template>

<style scoped>
.v-enter-active {
  transition: all 0.3s ease-out;
}
.v-enter-from {
  opacity: 0;
  transform: translate(0, 1rem);
}
.v-enter-to {
  opacity: 1;
  transform: translate(0, 0);
}
.v-leave-active {
  transition: all 0.2s ease-in;
}
.v-leave-from {
  opacity: 1;
  transform: translate(0, 0);
}
.v-leave-to {
  opacity: 0;
  transform: translate(0, 1rem);
}
@media (min-width: 640px) {
  .v-enter-from,
  .v-leave-to {
    transform: translate(0, 0) scale(0.95);
  }
  .v-enter-to,
  .v-leave-from {
    transform: translate(0, 0) scale(1);
  }
}
</style>
